import * as React from "react";
import { Col, Row, Button, Form } from "react-bootstrap";
import { $t, t_key } from "../plugins/i18n";
import { SessionInfo } from "../models/session_info";
import { useTranslation } from "react-i18next";
import { ServiceAuth } from "../models/application_info";
import LoginPasswordControl from "../components/login/login-password-control";
import { AuthService } from "../services/auth-service";
import EidasLogo from "assets/eIDAS.png";
import TaatLogo from "assets/taat-logo.png";
import { useLocation, useNavigate } from "react-router";
import { nl2br } from "../plugins/string-helpers";
import ChevronLeftIcon from "../components/icons/chevron-left-icon";
import Constants from "../Constants";
import TwoFactorInline from "../components/login/two-factor-inline";
import {TwoFactorChallenge} from "../models/two_factor";
import {useAlerts} from "../components/alerts/alert-provider";

export type LoginViewProps = {
  session: SessionInfo;
  underage?: boolean;
  onTwoFactorActiveChange?: (active: boolean) => void;
};

type ButtonInfo = {
  action: string;
  text: string;
  icon?: JSX.Element;
  variant: string;
  iconClass: string;
  position: number;
};

const LoginItems: { [key: string]: ButtonInfo } = {
  idcard: {
    action: "IDCard",
    text: t_key.login_view._id_card,
    icon: <img className="id-card-logo" alt="" />,
    variant: "link",
    iconClass: "landscape",
    position: 1,
  },
  mobileid: {
    action: "MobileID",
    text: t_key.login_view._mobile_id,
    icon: <img className="mobile-id-logo" alt="" />,
    variant: "link",
    iconClass: "portrait",
    position: 2,
  },
  smartid: {
    action: "SmartID",
    text: t_key.login_view._smart_id,
    icon: <img className="smart-id-logo" alt="" />,
    variant: "link",
    iconClass: "square",
    position: 3,
  },
  eidas: {
    action: "eIDAS",
    text: t_key.login_view._eidas_id,
    icon: <img src={EidasLogo} alt="" />,
    variant: "link",
    iconClass: "landscape",
    position: 4,
  },
  password: {
    action: "Password",
    text: t_key.login_view.login_with_password,
    icon: <img className="password-login-logo" alt="" />,
    variant: "link",
    iconClass: "bigger",
    position: 5,
  },
  harid: {
    action: "HarID",
    text: t_key.login_view.login_with_harid,
    icon: <img className="har-id-logo" alt="" />,
    variant: "link",
    iconClass: "bigger",
    position: 6,
  },
  taat: {
    action: "TAAT",
    text: t_key.login_view.login_with_taat,
    icon: <img src={TaatLogo} alt="" />,
    variant: "link",
    iconClass: "bigger",
    position: 7,
  },
}

const LoginView = (props: LoginViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert, removeAlert } = useAlerts();

  const [buttons, setButtons] = React.useState<ButtonInfo[]>([]);
  const [authType, setAuthType] = React.useState<string>("");
  const [, setReqStrong] = React.useState<boolean>(false);
  const [staySignedIn, setStaySignedIn] = React.useState<boolean>(false);

  const [inline2FA, setInline2FA] = React.useState<TwoFactorChallenge | null>(null);
  const handleSecondFactorRequired = (p: TwoFactorChallenge) => {
    if (p.methods.length === 0){
      showAlert({
        id:"2fa-no-methods",
        variant: "warning",
        content: (
            <p className="mb-0">
              {$t(t_key.login_view.tfa_no_methods)}
            </p>
        ),
        dismissible: true,
      });
      return;
    } else {
        removeAlert("2fa-no-methods");
        removeAlert("2fa-session-expired");
    }

    setInline2FA(p);
    props.onTwoFactorActiveChange?.(true);
  };

  const exitTwoFA = (reasonKey?: string) => {
    setInline2FA(null);
    props.onTwoFactorActiveChange?.(false);
    if (reasonKey) {
      showAlert({
        id: "2fa-session-expired",
        variant: "warning",
        content: (
            <p className="mb-0">
              {$t(reasonKey)}
            </p>
        ),
        dismissible: true,
      });
    } else {
      removeAlert("2fa-session-expired");
    }
  };

  const { i18n } = useTranslation();

  const authTypeClick = (action: string) => {
    setAuthType(action);

    switch (action) {
      case "Password":
        setAuthType(authType === "Password" ? "" : "Password");
        break;
      case "MobileID":
        authorizeTara(action);
        break;
      case "SmartID":
        authorizeTara(action);
        break;
      case "IDCard":
        authorizeTara(action);
        break;
      case "eIDAS":
        authorizeTara(action);
        break;
      case "HarID":
        authorizeHarId();
        break;
      case "TAAT":
        authorizeTAAT();
        break;
      default:
        break;
    }
  };

  const authorizeTara = (authType: string) => {
    if (!props.session.application) return;

    let returnUrl = props.underage
      ? `/services/${props.session.application?.appId}/register_underage/`
      : null;

    AuthService.authorizeTara(
      authType,
      i18n.language,
      props.session.application.appId,
      returnUrl,
      staySignedIn,
    );
  };

  const authorizeHarId = () => {
    if (!props.session.application) return;
    AuthService.authorizeHarId(i18n.language, props.session.application.appId, staySignedIn);
  };

  const authorizeTAAT = () => {
    if (!props.session.application) return;
    AuthService.authorizeTAAT(i18n.language, props.session.application.appId, staySignedIn);
  };

  const registerUnderage = () => {
    if (props.session.application != null) {
      navigate(`/services/${props.session.application?.appId}/register_underage/login?strong=1`)
    }
  };

  const defaultLogin = () => {
    if (props.session.application != null) {
      window.history.go(-1)
    }
  };
  const loadData = async () => {
    if (!props.session.application) return;

    let urlParams = new URLSearchParams(location.search);
    let strong = urlParams.get("strong") === "1";
    setReqStrong(strong);

    const authTypes = (strong ? props.session.application.serviceRegAuths : props.session.application.serviceAuths).map(
      (x: ServiceAuth) => x.authType.toLowerCase()
    );

    const authButtons = authTypes.map(authType => LoginItems[authType] );
    authButtons.sort((a, b) => a.position - b.position);

    setButtons(authButtons);
  };

  React.useEffect(() => {
    loadData();
  }, [location]);

  if (inline2FA) {
    return (
        <TwoFactorInline
            tempAuthId={inline2FA.tempAuthId}
            methods={inline2FA.methods}
            onCancel={exitTwoFA}
            recaptchaSiteKey={props.session.recaptcha_site_key}
        />
    );
  }

  return (
    <Row>
      <Col xs={12} md={6} lg={6} className="col1 text-break">
        {props.underage ? (
          <div className="back-bttn">
            <Button variant="link" onClick={defaultLogin}>
              <ChevronLeftIcon /> <span>{$t(t_key.buttons.back)}</span>
            </Button>
          </div>
        ) : null}
        <h1>
          {$t(
            props.underage
              ? props.session.registration?.type == Constants.SERVICE_TYPE_RARA
                ? t_key.login_view.rara_register_for_minor_title
                : t_key.login_view.national_register_for_minor_title
              : t_key.login_view.title
          )}
        </h1>
        <p>
          {nl2br(
            $t(
              props.underage
                ? props.session.registration?.type == Constants.SERVICE_TYPE_RARA
                  ? t_key.login_view.rara_register_for_minor_description
                  : t_key.login_view.national_register_for_minor_description
                : t_key.login_view.description
            )
          )}
        </p>
      </Col>
      <Col xs={12} md={6} lg={6} className="col2 d-flex flex-column gap-3">
        <div id={Constants.MAIN_CONTENT} className="d-flex gap-2 flex-wrap justify-content-around align-items-end">
          {buttons?.map((b) => (
            <>
              <Button
                className={b.icon ? "btn-icon mt-auto" : "w-100"}
                key={b.action}
                variant={b.variant}
                onClick={() => authTypeClick(b.action)}
                aria-expanded={b.action === "Password" ? (authType === "Password") : undefined}
              >
                <div className={"mt-auto m-auto " + b.iconClass}>{b.icon}</div>
                <div className="mt-auto m-auto pt-1">{$t(b.text)}</div>
              </Button>

              {authType === "Password" && b.action == "Password" &&
                <LoginPasswordControl
                  session={props.session}
                  underage={props.underage}
                  staySignedIn={staySignedIn}
                  onTwoFactorRequired={handleSecondFactorRequired}
                />
              }
            </>
          ))}
        </div>

        <div className="w-100 border-bottom border-grey-light border-1 mt-5 mb-5" />
        
        {props.session.registration?.canStaySignedIn && (
          <Row>
            <Col
              xs={12}
              className="d-flex justify-content-center align-items-center gap-2"
            >
              <Form.Check
                type="checkbox"
                id="stay-signed-in"
                checked={staySignedIn}
                onChange={(e) => setStaySignedIn(e.target.checked)}
                label={<span className="fw-medium">{$t(t_key.login_view.stay_signed_in)}</span>}
              />
            </Col>
          </Row>
        )}

        {props.underage || !props.session.registration?.regUnderage ? null : (
          <Row>
            <Col
              xs={12}
              className="d-flex justify-content-center align-items-center d-flex gap-2"
            >
              <span className="fw-medium">{$t(t_key.login_view.register_for_minor)}</span>
              <span className="link fw-medium span-link" onClick={registerUnderage} tabIndex={0} role="link">
                {$t(t_key.login_view.register_for_minor_link)}
              </span>
            </Col>
          </Row>
        )}
        
      </Col>
    </Row>
  );
};
LoginView.displayName = "LoginView"; //for chrome to show name instead of anonymous.

export default LoginView;
